/**
 * Composable for Tidal ID validation
 * Provides format validation and optional API validation
 */

import { ref } from 'vue'

export function useTidalValidation() {
  const isValidating = ref(false)

  /**
   * Validate Tidal ID format
   * @param {string} tidalId - Tidal ID to validate
   * @returns {boolean} - Whether the format is valid
   */
  const validateTidalIdFormat = (tidalId) => {
    if (!tidalId) return true // Empty is valid (optional field)
    
    // Tidal IDs are typically 8-10 digit numbers
    const tidalIdPattern = /^\d{8,10}$/
    return tidalIdPattern.test(tidalId.toString())
  }

  /**
   * Validate Tidal ID with optional API check
   * @param {string} tidalId - Tidal ID to validate
   * @param {Object} options - Validation options
   * @returns {Promise<Object>} - Validation result
   */
  const validateTidalId = async (tidalId, options = {}) => {
    const { skipApiCheck = true } = options

    // Format validation first
    if (!validateTidalIdFormat(tidalId)) {
      return {
        isValid: false,
        error: 'Tidal ID must be 8-10 digits',
        type: 'format'
      }
    }

    // Skip API validation for now (could be implemented later)
    if (skipApiCheck) {
      return {
        isValid: true,
        error: null,
        type: 'format'
      }
    }

    // Future: API validation could be implemented here
    isValidating.value = true

    try {
      // Placeholder for future API validation
      await new Promise(resolve => setTimeout(resolve, 500))

      return {
        isValid: true,
        error: null,
        type: 'api'
      }
    } catch (error) {
      return {
        isValid: false,
        error: 'Could not verify Tidal ID',
        type: 'api'
      }
    } finally {
      isValidating.value = false
    }
  }

  /**
   * Generate Tidal album URL from ID
   * @param {string} tidalId - Tidal album ID
   * @returns {string} - Tidal album URL
   */
  const getTidalUrl = (tidalId) => {
    if (!tidalId) return null
    return `https://tidal.com/browse/album/${tidalId}`
  }

  /**
   * Extract Tidal ID from various input formats
   * @param {string} input - Input that might contain a Tidal ID
   * @returns {string|null} - Extracted Tidal ID or null
   */
  const extractTidalId = (input) => {
    if (!input) return null

    // Remove whitespace
    const cleaned = input.trim()

    // If it's already a valid format, return it
    if (validateTidalIdFormat(cleaned)) {
      return cleaned
    }

    // Try to extract from URL
    const urlPatterns = [
      /tidal\.com\/browse\/album\/(\d{8,10})/i,
      /tidal\.com\/album\/(\d{8,10})/i
    ]

    for (const pattern of urlPatterns) {
      const match = cleaned.match(pattern)
      if (match && match[1]) {
        return match[1]
      }
    }

    // Try to find any 8-10 digit sequence
    const digitMatch = cleaned.match(/\d{8,10}/)
    if (digitMatch) {
      return digitMatch[0]
    }

    return null
  }

  /**
   * Validate and clean Tidal ID input
   * @param {string} input - Raw input
   * @returns {Object} - Cleaned result
   */
  const validateAndCleanInput = (input) => {
    const extracted = extractTidalId(input)
    const isValid = validateTidalIdFormat(extracted)

    return {
      cleanedValue: extracted,
      isValid,
      wasModified: input !== extracted,
      error: !isValid && extracted ? 'Invalid Tidal ID format' : null
    }
  }

  return {
    isValidating,
    validateTidalId,
    validateTidalIdFormat,
    getTidalUrl,
    extractTidalId,
    validateAndCleanInput
  }
}